const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_URL = `${BASE_URL}/api`;

// Helper to get authorization headers
const getHeaders = (isMultipart = false) => {
  const token = localStorage.getItem('admin_token');
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (!isMultipart) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

// Asset URL resolver
export const getAssetUrl = (filename) => {
  if (!filename) return '';
  if (filename.startsWith('http') || filename.startsWith('/') || filename.startsWith('data:')) {
    return filename; // It is already a full URL, public folder path or base64
  }
  return `${BASE_URL}/uploads/${filename}`;
};

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Login failed.');
    
    // Store token and user details
    localStorage.setItem('admin_token', data.token);
    localStorage.setItem('admin_user', JSON.stringify(data.admin));
    return data;
  },

  logout: () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('admin_token');
  },

  updatePassword: async (currentPassword, newPassword) => {
    const res = await fetch(`${API_URL}/auth/update-password`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ currentPassword, newPassword })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update password.');
    return data;
  }
};

// Portfolio CRUD API
export const portfolioAPI = {
  // Public fetch
  getPortfolioData: async () => {
    const res = await fetch(`${API_URL}/portfolio/data`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch portfolio data.');
    return data;
  },

  // Hero Section
  updateHero: async (formData) => {
    const res = await fetch(`${API_URL}/portfolio/hero`, {
      method: 'PUT',
      headers: getHeaders(true),
      body: formData
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update hero section.');
    return data;
  },

  // About Section
  updateAbout: async (formData) => {
    const res = await fetch(`${API_URL}/portfolio/about`, {
      method: 'PUT',
      headers: getHeaders(true),
      body: formData
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update about section.');
    return data;
  },

  // Highlights CRUD
  addHighlight: async (highlightData) => {
    const res = await fetch(`${API_URL}/portfolio/highlights`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(highlightData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to add highlight.');
    return data;
  },

  updateHighlight: async (id, highlightData) => {
    const res = await fetch(`${API_URL}/portfolio/highlights/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(highlightData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update highlight.');
    return data;
  },

  deleteHighlight: async (id) => {
    const res = await fetch(`${API_URL}/portfolio/highlights/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete highlight.');
    return data;
  },

  // Skills CRUD
  addSkill: async (skillData) => {
    const res = await fetch(`${API_URL}/portfolio/skills`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(skillData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to add skill.');
    return data;
  },

  updateSkill: async (id, skillData) => {
    const res = await fetch(`${API_URL}/portfolio/skills/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(skillData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update skill.');
    return data;
  },

  deleteSkill: async (id) => {
    const res = await fetch(`${API_URL}/portfolio/skills/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete skill.');
    return data;
  },

  // Projects CRUD
  addProject: async (projectData) => {
    const res = await fetch(`${API_URL}/portfolio/projects`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(projectData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to add project.');
    return data;
  },

  updateProject: async (id, projectData) => {
    const res = await fetch(`${API_URL}/portfolio/projects/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(projectData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update project.');
    return data;
  },

  deleteProject: async (id) => {
    const res = await fetch(`${API_URL}/portfolio/projects/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete project.');
    return data;
  },

  // Contact Info
  updateContact: async (contactData) => {
    const res = await fetch(`${API_URL}/portfolio/contact`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(contactData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update contact info.');
    return data;
  }
};
